import Vue from "vue";
import VueApollo from "vue-apollo";
import router from "./router/index";
import { from } from "apollo-link";
import { onError } from "apollo-link-error";
import { createApolloClient, restartWebsockets } from "vue-cli-plugin-apollo/graphql-client";
import { BatchHttpLink } from "apollo-link-batch-http";
import { InMemoryCache, defaultDataIdFromObject, IntrospectionFragmentMatcher } from "apollo-cache-inmemory";
import cookie from "js-cookie";
import { typeDefs, resolvers } from "./graphql/resolvers";
import defaultCache from "./graphql/defaultCache";
import { ApolloLink, concat } from "apollo-link";

// cookie.set("organisation-school", "A1329183376");

// Install the vue plugin
Vue.use(VueApollo);

// Name of the localStorage item
const AUTH_TOKEN = "apollo-token";

const cache = new InMemoryCache({
	dataIdFromObject: (object) => {
		switch (object.__typename) {
			case "Organisation":
				return object.uuid;

			default:
				return defaultDataIdFromObject(object);
		}
	},
});

// Config
const privateConfig = {
	// You can use `https` for secure connection (recommended in production)
	httpEndpoint: process.env.GRAPH_API_PRIVATE,
	// You can use `wss` for secure connection (recommended in production)
	// Use `null` to disable subscriptions
	wsEndpoint: null,
	// LocalStorage token
	tokenName: AUTH_TOKEN,
	// Enable Automatic Query persisting with Apollo Engine
	persisting: false,
	// Use websockets for everything (no HTTP)
	// You need to pass a `wsEndpoint` for this to work
	websocketsOnly: false,
	// Is being rendered on the server?
	ssr: false,

	// Override default http link
	link: new BatchHttpLink({
		uri: process.env.GRAPH_API_PRIVATE,
		batchInterval: 20,
		headers: {
			// Organisation users can login to any school, this is done with cookies.
			// We are passing the desired school as a header with each graphql request
			"x-organisation-school": cookie.get("organisation-school") || "none",
		},
		fetch: (...args) => {
			return window.fetch(...args);
		},
	}),

	// Override default cache
	cache,

	// Override the way the Authorization header is set
	getAuth: (tokenName, second) => {
		var name = "wonde-edu-login" + "=";
		var decodedCookie = decodeURIComponent(document.cookie);
		var ca = decodedCookie.split(";");
		for (var i = 0; i < ca.length; i++) {
			var c = ca[i];
			while (c.charAt(0) == " ") {
				c = c.substring(1);
			}
			if (c.indexOf(name) == 0) {
				return c.substring(name.length, c.length);
			}
		}
		return "";
	},

	// Additional ApolloClient options
	// apollo: { ... }

	// Client local data
	typeDefs,
	clientState: {
		resolvers,
	},
};

const privateConfigNonBatch = {
	// You can use `https` for secure connection (recommended in production)
	httpEndpoint: process.env.GRAPH_API_PRIVATE,
	// You can use `wss` for secure connection (recommended in production)
	// Use `null` to disable subscriptions
	wsEndpoint: null,
	// LocalStorage token
	tokenName: AUTH_TOKEN,
	// Enable Automatic Query persisting with Apollo Engine
	persisting: false,
	// Use websockets for everything (no HTTP)
	// You need to pass a `wsEndpoint` for this to work
	websocketsOnly: false,
	// Is being rendered on the server?
	ssr: false,

	// Override default http link
	link: from([
		onError((error) => {
			if (error.networkError.result.error === "Unauthorised" && window.location.pathname !== "/logout") {
				// window.location.replace("/");
				console.log("Homepage...");
			}
		}),
		new BatchHttpLink({
			uri: process.env.GRAPH_API_PRIVATE,
			batchInterval: 20,
			headers: {
				// Organisation users can login to any school, this is done with cookies.
				// We are passing the desired school as a header with each graphql request
				"x-organisation-school": cookie.get("organisation-school") || "none",
			},
			fetch: (...args) => {
				return window.fetch(...args);
			},
		}),
	]),

	// Override default cache
	cache: new InMemoryCache({
		dataIdFromObject: (object) => {
			switch (object.__typename) {
				case "Organisation":
					return object.uuid;

				default:
					return defaultDataIdFromObject(object);
			}
		},
	}),

	// Override the way the Authorization header is set
	getAuth: (tokenName, second) => {
		var name = "wonde-edu-login" + "=";
		var decodedCookie = decodeURIComponent(document.cookie);
		var ca = decodedCookie.split(";");
		for (var i = 0; i < ca.length; i++) {
			var c = ca[i];
			while (c.charAt(0) == " ") {
				c = c.substring(1);
			}
			if (c.indexOf(name) == 0) {
				return c.substring(name.length, c.length);
			}
		}
		return "";
	},

	// Additional ApolloClient options
	// apollo: { ... }

	// Client local data (see apollo-link-state)
	// clientState: { resolvers: { ... }, defaults: { ... } }
};

const publicConfig = {
	// You can use `https` for secure connection (recommended in production)
	httpEndpoint: process.env.GRAPH_API_PUBLIC,
	// You can use `wss` for secure connection (recommended in production)
	// Use `null` to disable subscriptions
	wsEndpoint: null,
	// LocalStorage token
	tokenName: AUTH_TOKEN,
	// Enable Automatic Query persisting with Apollo Engine
	persisting: false,
	// Use websockets for everything (no HTTP)
	// You need to pass a `wsEndpoint` for this to work
	websocketsOnly: false,
	// Is being rendered on the server?
	ssr: false,

	// Override default http link
	link: new BatchHttpLink({
		uri: process.env.GRAPH_API_PUBLIC,
		batchInterval: 20,
		fetch: (...args) => {
			return window.fetch(...args);
		},
	}),

	// Override default cache
	// cache: myCache

	// Override the way the Authorization header is set
	// getAuth: (tokenName) => ...

	// Additional ApolloClient options
	// apollo: { ... }

	// Client local data (see apollo-link-state)
	// clientState: { resolvers: { ... }, defaults: { ... } }
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: {
		__schema: {
			types: [
				{
					kind: "UNION",
					name: "SchoolApprovalAuditsFullItems",
					possibleTypes: [
						{
							name: "ApprovalAudit",
						},
						{
							name: "CompanySchoolHistory",
						},
					],
				},
			],
		},
	},
});

const nextConfig = {
	httpEndpoint: process.env.GRAPH_API_NEXT,
	wsEndpoint: null,
	tokenName: "wonde-auth-token",
	persisting: false,
	websocketsOnly: false,
	ssr: false,
	link: concat(
		new ApolloLink((operation, forward) => {
			operation.setContext(({ headers = {} }) => ({
				headers: {
					...headers,
					origin_path: window.location.href,
				},
			}));

			return forward(operation);
		}),
		new BatchHttpLink({
			uri: process.env.GRAPH_API_NEXT,
			batchInterval: 20,
			fetch: (...args) => {
				return window.fetch(...args);
			},
		})
	),
	getAuth: (tokenName) => localStorage.getItem(tokenName),
	cache: new InMemoryCache({
		fragmentMatcher,
	}),
};

cache.writeData({ data: defaultCache });

// Call this in the Vue app file
export function createProvider(options = {}) {
	// Next Apollo Client -> Services v2
	const { apolloClient: nextApolloClient, wsClient: nextWsClient } = createApolloClient({
		...nextConfig,
		...options,
	});
	nextApolloClient.wsClient = nextWsClient;

	// Create apollo client (public)
	const { apolloClient: publicApolloClient, wsClient: publicWsClient } = createApolloClient({
		...publicConfig,
		...options,
	});
	publicApolloClient.wsClient = publicWsClient;

	// Create apollo client (private)
	const { apolloClient: privateApolloClient, wsClient: privateWsClient } = createApolloClient({
		...privateConfig,
		...options,
	});
	const { apolloClient: privateApolloClientNonBatch, wsClient: privateWsClientNonBatch } = createApolloClient({
		...privateConfigNonBatch,
		...options,
	});
	privateApolloClientNonBatch.wsClient = privateWsClientNonBatch;
	privateApolloClient.wsClient = privateWsClient;

	window.privateApolloClient = privateApolloClient;
	window.privateApolloClientNonBatch = privateApolloClientNonBatch;

	privateApolloClient.onResetStore(() => {
		privateApolloClient.cache.writeData({ data: defaultCache });
	});

	// Create vue apollo provider
	const apolloProvider = new VueApollo({
		clients: {
			next: nextApolloClient,
			public: publicApolloClient,
			private: privateApolloClient,
			nonbatch: privateApolloClientNonBatch,
		},
		defaultClient: privateApolloClient,
		errorHandler(error) {
			for (let index = 0; index < error.graphQLErrors.length; index++) {
				const r = error.graphQLErrors[index];

				if (r.message == "404") {
					return router.push({ name: "dashboard" });
				}

				if (["RateError"].includes(r.name)) {
					window.globalErrors.push(r);
				}
			}

			if ((error.networkError && error.networkError.result && error.networkError.result.error === "Unauthorised") || (error.message === 'GraphQL error: Invalid JWT') && window.location.pathname !== "/logout") {
				window.location.replace("/");
			}

			// eslint-disable-next-line no-console
			console.log("%cError", "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;", error.message);
		},
	});

	return apolloProvider;
}

// Manually call this when user log in
export async function onLogin(apolloClient, token) {
	localStorage.setItem(AUTH_TOKEN, token);
	if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
	try {
		await apolloClient.resetStore();
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("%cError on cache reset (login)", "color: orange;", e.message);
	}
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
	localStorage.removeItem(AUTH_TOKEN);
	if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
	try {
		await apolloClient.resetStore();
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log("%cError on cache reset (logout)", "color: orange;", e.message);
	}
}



// WEBPACK FOOTER //
// ./src/vue-apollo.js